import React, { useState } from 'react';

import {
  Box,
  Button,
  colors,
  Flex,
  spacing,
  Typography,
  useI18nContext,
} from '@procore/core-react';
import styled from 'styled-components';
import { Filter } from '@procore/core-icons';
import Fuse from 'fuse.js';
import { FUSE_CONFIG } from '@/react/shared/utils/fuseConfig';
import { useAppContext } from '@/react/context/AppContext';
import { useVisibility } from '@/react/hooks/useVisibility';
import { useMediaQuery } from '@/react/hooks/useMediaQuery';
import { useActiveCategory } from '@/react/hooks/useActiveCategory';

import {
  AppCard,
  ListingsSidebar,
  NoResults,
  Navbar,
  SearchBar,
  MobileOverlay,
  MobileOverlayHeader,
  MobileOverlayBody,
  CategoryTitleSection,
  TopCategories,
} from '@/react/components';

const SidebarWrapper = styled(Box)`
  display: block;
  width: 296px;
  margin-right: 48px;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const ListingsAppsWrapper = styled(Box)`
  width: 100%;

  @media (min-width: 1024px) {
    width: 1048px;
  }
`;

const GridContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ListingContainer = styled(Flex)`
  max-width: 1440px;
  margin: 48px auto;
  padding: 0 48px;
  flex-direction: row;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const CommercialHero = styled(Box)`
  background: url(/images/marketplace-listings-hero.png) no-repeat top center;
  background-size: cover;
  height: 400px;
`;

const FedrampHero = styled(CommercialHero)`
  background: url(/images/marketplace_header_fedramp.png) no-repeat top center;
  background-size: cover;
`;

const MobileFiltersButton = styled(Button)`
  display: none;
  letter-spacing: 0.15;

  @media (max-width: 1024px) {
    display: flex;
    margin-bottom: ${spacing.xxl}px;
    align-self: flex-start;
  }
`;

const MarketplaceListing = () => {
  const [isVisible, elementRef] = useVisibility(100);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { listing } = useAppContext();
  const i18n = useI18nContext();
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const { apps, categories, query } = listing;
  const activeCategory = useActiveCategory();

  const toggleFilters = () => setIsFiltersOpen(!isFiltersOpen);

  apps
    .filter((app) => app.name.startsWith('Arcoro'))
    .map(
      (app) =>
        (app.search_description = app.search_description.replace(/^/, 'HR '))
    );

  const fuse = new Fuse(apps, FUSE_CONFIG);
  const listings = query
    ? fuse.search(query).map((result) => result.item)
    : apps;

  listings
    .filter((app) => app.name.startsWith(' '))
    .map(
      (app) =>
        (app.search_description = app.search_description.replace(/^HR /, ''))
    );

  const Hero = window.IS_FEDRAMP ? FedrampHero : CommercialHero;

  return (
    <>
      <Navbar isSearchVisible={!isVisible} />

      <Hero
        className="hero-commercial"
        display="flex-column"
        padding="xl"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="flex-start"
          style={{ maxWidth: '1200px', width: '100%', paddingLeft: '2rem' }}
        >
          <Typography
            className="font-procore hero-title"
            intent="h1"
            color="gray98"
            weight="bold"
            style={{
              fontSize: '52px',
              lineHeight: '48px',
              marginBottom: `${spacing.xl}px`,
            }}
          >
            {i18n.t('hero.title')}
          </Typography>
          <Typography
            className="font-procore hero-tagline"
            intent="h2"
            color="gray98"
            weight="normal"
            style={{
              fontSize: '24px',
              lineHeight: '32px',
              marginBottom: `${spacing.md}px`,
            }}
          >
            {i18n.t('hero.tagline')}
          </Typography>
          <Box
            ref={elementRef}
            display="flex"
            style={{ width: '100%', maxWidth: '700px', paddingRight: '40px' }}
          >
            <SearchBar
              hasAutoFocus
              styledSearch={{
                backgroundColor: colors.white,
                hoverColor: colors.gray96,
              }}
            />
          </Box>
        </Box>
      </Hero>

      <ListingContainer>
        {categories.length > 0 && (
          <>
            <MobileFiltersButton
              variant="secondary"
              size="lg"
              icon={<Filter size="sm" />}
              onClick={toggleFilters}
              data-testid="mobile-filters-button"
            >
              <Typography intent="h3">{i18n.t('filters.title')}</Typography>
            </MobileFiltersButton>

            <SidebarWrapper>
              <Box marginBottom="lg">
                <Typography intent="h2" weight="bold">
                  {i18n.t('filters.title')}
                </Typography>
              </Box>
              <ListingsSidebar categories={categories} testId="desktop" />
            </SidebarWrapper>
          </>
        )}

        <ListingsAppsWrapper>
          {listings.length > 0 ? (
            <>
              {activeCategory?.showTitleSection && (
                <>
                  <Box marginBottom="xxl">
                    <TopCategories />
                  </Box>

                  <CategoryTitleSection
                    title={i18n.t(activeCategory.title)}
                    subtitle={i18n.t(activeCategory.subtitle)}
                  />
                </>
              )}
              <GridContainer>
                {listings.map((listing) => (
                  <AppCard app={listing} key={listing.slug} />
                ))}
              </GridContainer>
            </>
          ) : (
            <NoResults />
          )}
        </ListingsAppsWrapper>
      </ListingContainer>

      {/* Mobile filters overlay */}
      {isMobile && (
        <MobileOverlay
          isOpen={isFiltersOpen}
          onClose={toggleFilters}
          testId="mobile-filters-overlay"
        >
          <MobileOverlayHeader onClose={toggleFilters}>
            <Typography intent="h1" weight="semibold">
              {i18n.t('filters.title')}
            </Typography>
          </MobileOverlayHeader>

          <MobileOverlayBody>
            <ListingsSidebar
              categories={categories}
              onClose={toggleFilters}
              testId="mobile"
            />
          </MobileOverlayBody>
        </MobileOverlay>
      )}
    </>
  );
};

export default MarketplaceListing;
